import * as React from "react";
import { useEffect } from "react";
import { PageProps } from "gatsby";
import * as styles from "./styles.module.scss";
import { StaticImage } from "gatsby-plugin-image";
import classNames from "classnames";
import { Breadcrumb } from "../../../components/breadcrumb/breadcrumb";
import Layout from "../../../components/layout/layout";
import Container from "../../../components/container/container";
import Heading from "../../../components/basic-components/heading/heading";
import { ProductBar } from "../../../components/product-bar/product-bar";
import { Level } from "react-accessible-headings";
import scrollTo from "gatsby-plugin-smoothscroll";
import { Helmet } from "react-helmet";
import blogImage from "../../../images/cozum/ses-kisikligi/Content-image.webp";
import { AnchorLink } from "gatsby-plugin-anchor-links";

const CoughPage: React.FC<PageProps> = ({ location }: PageProps) => {
  useEffect(() => {
    if (location.hash) {
      scrollTo(location.hash);
    }
  }, []);
  return (
    <Layout
      stickyHeader={false}
      seoConfig={{
        title: "Ses Kısıklığı Nedenleri Nelerdir, Nasıl Geçer?",
        description:
          "Ses Kısıklığı Nedenleri Nelerdir, Nasıl Geçer? gibi soruların yanıtını içeriğimizi okuyarak öğrenmek için sayfamızı ziyaret edin!",
      }}
    >
      <Helmet>
        <script type="application/ld+json">
          {`
            {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {"@type": "ListItem","position": 1,"name": "Ana Sayfa","item": "https://www.bisolnatur.com.tr/"},
                  {"@type": "ListItem","position": 2,"name": "Öksürük","item": "https://www.bisolnatur.com.tr/oksuruk/"},
                  {"@type": "ListItem","position": 3,"name": "Ses Kısıklığı Nedenleri Nelerdir, Nasıl Geçer?","item": "https://www.bisolnatur.com.tr/oksuruk/ses-kisikligi-nedenleri-nelerdir-nasil-gecer"}
                ]
            }
          `}
        </script>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "${location.href}"
              },
              "headline": "Ses Kısıklığı Nedenleri Nelerdir, Nasıl Geçer?",
              "image": "${location.origin + blogImage}",  
              "author": {
                "@type": "Organization",
                "name": "Bisolnatur",
                "url": "${location.origin}"
              },  
              "publisher": {
                "@type": "Organization",
                "name": "Bisolnatur",
                "logo": {
                  "@type": "ImageObject",
                  "url": "${location.origin}/logo.svg"
                }
              },
              "datePublished": "2022-01-27",
              "dateModified": "2022-01-27"
            }
          `}
        </script>
        <script type="application/ld+json">
          {`
            {"@context":"https://schema.org","@type":"FAQPage","mainEntity":[{"@type":"Question","name":"Ses Kısıklığı Nedir?","acceptedAnswer":{"@type":"Answer","text":"Ses kısıklığı, sesin kalitesinde, frekansında, yüksekliğinde veya sesin oluşturulması için harcanan eforda değişiklik meydana gelmesi olarak tanımlanabilir. Bu durum kişinin diğer bireyler ile iletişimini olumsuz yönde etkileyerek yaşam kalitesinin azalmasına neden olabilir."}},{"@type":"Question","name":"Ses Kısıklığı Nedenleri Nelerdir?","acceptedAnswer":{"@type":"Answer","text":"Ses kısıklığı nedenleri incelendiğinde ilk sırada larenjit adı verilen durum yer alır. Larenjit gelişimi, akut (ani başlangıçlı ve kısa süreli) veya kronik (uzun süreli) olarak meydana gelebilir. Akut larenjit olguları genellikle soğuk algınlığı gibi üst solunum yolu enfeksiyonlarından kaynaklanır. Soğuk algınlığı dışında çeşitli eylemler de larinks bölgesinde inflamasyona neden olabilir. Bağırma, yüksek sesle konuşma, yüksek sesle şarkı söyleme, ses tellerini dinlendirmeden uzun süre boyunca konuşma yapılması ve çok tiz veya pes tonlarda uzun süreli olarak konuşulması, ses tellerinin bulunduğu bölgede inflamasyona neden olabilecek durumlar arasında yer alır."}},{"@type":"Question","name":"Ses Kısıklığı En Çok Kimlerde Görülmektedir?","acceptedAnswer":{"@type":"Answer","text":"Ses kısıklığı en sık olarak larinks bölgesinin inflamasyonunu tetikleyebilen faktörlere sahip kişilerde görülür. Soğuk algınlığı, bağırma veya yüksek sesle şarkı söylemeyi gerektiren meslekler, bakteriyel enfeksiyonlar veya alkol tüketimi olan kişilerde, ses kısıklığına karşı bir yatkınlık söz konusu olabilir. Bu faktörlere ek olarak, hava kirliliği bulunan bölgelerde yaşanması, çeşitli alerjen veya kimyasal maddelerin havayolu ile vücuda alınması, reflü, sık sinüs enfeksiyonu geçirilmesi, tütün kullanımı veya pasif içicilik gibi durumlar da ses kısıklığı riskini artırıcı özellik gösterebilen faktörler arasında yer alır."}},{"@type":"Question","name":"Ses Kısıklığına Ne İyi Gelir?","acceptedAnswer":{"@type":"Answer","text":"Ses kısıklığının meydana gelmesi genellikle larenjit olarak isimlendirilen iltihabi durumdan kaynaklanır. Ses tellerinin aşırı kullanılması veya soğuk algınlığı gibi durumlarda, larenjit ortaya çıkabilir. Larinks bölgesinde yer alan ses telleri, konuşma sırasında düzgünce açılıp kapanma hareketi yapar. Bu hareket sırasında aralarından geçen hava, ses tellerinin titreştirilmesini ve sesin oluşturulmasını sağlar. Ses tellerinin bulunduğu bölgede meydana gelen inflamasyon ve ödem, havanın bu bölgeden geçişini engelleyerek sesin normalden daha kısık oluşmasına neden olabilir. Çeşitli uygulamalar, basit nedenlerle oluşan ses kısıklığı olgularında durumun kontrol altına alınmasına katkı sağlayabilir."}}]}
          `}
        </script>
      </Helmet>
      <Breadcrumb
        pathname={location.pathname}
        noBg={true}
        className={styles.breadcrumb}
      />
      <Container fullWidth>
        <div className={classNames(styles.header)}>
          <StaticImage
            className={styles.image}
            src="../../../images/cozum/ses-kisikligi/Content-image.webp"
            alt="Ses kısıklığı yaşayan erkek görseli"
            width={1680}
            height={450}
          />
          <Container>
            <Heading>
              Ses Kısıklığı Nedenleri
              <br />
              Nelerdir, Nasıl Geçer?
            </Heading>
            <ul>
              <li>
                <AnchorLink
                  className="blue"
                  to="#ses-kisikligi-nedir"
                  title="Ses Kısıklığı Nedir?"
                >
                  Ses Kısıklığı Nedir?
                </AnchorLink>
              </li>
              <li>
                <AnchorLink
                  className="blue"
                  to="#ses-kisikligi-nedenleri-nelerdir"
                  title="Ses Kısıklığı Nedenleri Nelerdir? "
                >
                  Ses Kısıklığı Nedenleri Nelerdir?
                </AnchorLink>
              </li>
              <li>
                <AnchorLink
                  className="blue"
                  to="#ses-kisikligi-en-cok-kimlerde-gorulur"
                  title="Ses Kısıklığı En Çok Kimlerde Görülmektedir?"
                >
                  Ses Kısıklığı En Çok Kimlerde Görülmektedir?
                </AnchorLink>
              </li>
              <li>
                <AnchorLink
                  className="blue"
                  to="#ses-kisikligina-ne-iyi-gelir"
                  title="Ses Kısıklığına Ne İyi Gelir?"
                >
                  Ses Kısıklığına Ne İyi Gelir?
                </AnchorLink>
              </li>
            </ul>
          </Container>
        </div>
      </Container>
      <Container>
        <div className={styles.holder}>
          <p id="ses-kisikligi-nedir" className="extra-bold">
            Seste kısılma meydana gelmesi, bazı kişilerin ses tonunun bir miktar
            düşmesi şeklinde ortaya çıkarken bazı kişilerde ise sesin neredeyse
            duyulmayacak seviyeye gelmesi olarak da kendisini gösterebilir. Ses
            kısıklığı genel olarak vokal kord olarak isimlendirilen ses
            tellerinin normal titreşimini engelleyen herhangi bir durumdan
            kaynaklanıyor olabilir. Bu bölgede ödem meydana gelmesi, inflamasyon
            (iltihabi durumlar) ve polipler, ses tellerinin hareketlerini
            olumsuz yönde etkileyerek ses kısıklığı oluşturabilecek durumlar
            arasında yer alır. Bu durumların gelişmesinde çoğunlukla soğuk
            algınlığı veya alerjik durumlar rol oynar.
          </p>
          <section className={styles.whatHolder}>
            <Level>
              <h2 className="orange underline">Ses Kısıklığı Nedir?</h2>
              <p>
                Ses kısıklığı, sesin kalitesinde, frekansında, yüksekliğinde
                veya sesin oluşturulması için harcanan eforda değişiklik meydana
                gelmesi olarak tanımlanabilir. Bu durum kişinin diğer bireyler
                ile iletişimini olumsuz yönde etkileyerek yaşam kalitesinin
                azalmasına neden olabilir.
                <br />
                <br />
                Ses kısıklığı, normal ses düzeyinin anormal bir şekilde azalması
                olarak da kısaca ifade edilebilir. Toplum genelinde oldukça sık
                görülen bu durum, genellikle boğazda tahriş hissi ile
                birliktelik gösterir. Seste kısıklık meydana gelmesi halinde
                çatlak, güçsüz ve düşük tonda bir ses oluşur. Bu sorunun
                temelinde ise ses tellerinin bulunduğu bölge olan larinkste
                çeşitli nedenlerle oluşan larenjit bulunur. Larenjit adı verilen
                durum, ses tellerinin aşırı kullanılması, çeşitli nedenlerle
                tahriş olması veya soğuk algınlığı gibi enfeksiyon durumlarına
                bağlı olarak oluşabilir. 7-10 günden daha fazla süreyle devam
                eden kalıcı ses kısıklığında, ileri araştırmaların yapılabilmesi
                için sağlık kuruluşlarına başvurulmasında fayda vardır.
                <br />
                <br />
                Dinlenme durumunda ses tellerini ihtiva eden katlantılar açık
                konumdadır. Konuşma, bağırma ya da şarkı söyleme gibi, bu
                yapıların kullanılacağı bir eyleme karar verilmesi halinde ise
                birçok farklı faktör bir arada çalışarak duyulabilir bir sesin
                oluşmasına yardımcı olur. Sesin oluşturulması aşamasında bu
                faktörlerden herhangi birinin olumsuz yönde etkilenmesi, ses
                kısıklığı şeklinde kendisini gösterebilir. Ses oluşturulurken
                ilk aşamada vokal katlantılar birbirine yaklaşır. Bu yapıların
                birbirine yaklaşmasında sorun oluşması, katlantıların
                hareketlerini kontrol eden sinirlerde oluşan bir problemden
                kaynaklanabilir. Bu durumun sonucunda ise ses kısıklığı şikayeti
                ortaya çıkar. Sesin oluşturulmasının ikinci aşamasında birbirine
                yaklaşan vokal katlantıların arasından hava geçerek, bu
                yapıların titreştirilmesi sağlanır. Birbirine yapışık ve kapalı
                şekilde duran bu katlantıların arasından hava geçişini
                engelleyecek herhangi bir problem ses kısıklığına neden
                olabilir.
                <br />
                <br />
                Vokal katlantıların hareketi ve sonrasında meydana gelen
                titreşim aşaması sonucunda oluşturulan sesin, çevreden
                algılanabilmesi için vücudu terk etmesi gerekir. Hava akışının
                dışarıya akışını engelleyen ağız, burun ve boğaz bölgesindeki
                çeşitli problemler, ses kısıklığı şikayeti ile kendisini
                gösterebilir.
                <br />
                <br />
              </p>
              <StaticImage
                className={styles.image}
                src="../../../images/cozum/ses-kisikligi/img.webp"
                alt="Boğazlı kazak giyen kadın görseli"
                width={792}
                height={430}
              />
              <br />
              <br />
              <Level>
                <Heading id="ses-kisikligi-nedenleri-nelerdir">
                  Ses Kısıklığı Nedenleri Nelerdir?
                </Heading>
              </Level>
              <p>
                <br />
                Ses kısıklığı nedenleri incelendiğinde ilk sırada larenjit adı
                verilen durum yer alır. Larenjit gelişimi, akut (ani başlangıçlı
                ve kısa süreli) veya kronik (uzun süreli) olarak meydana
                gelebilir. Akut larenjit olguları genellikle soğuk algınlığı
                gibi üst solunum yolu enfeksiyonlarından kaynaklanır. Soğuk
                algınlığı dışında çeşitli eylemler de larinks bölgesinde
                inflamasyona neden olabilir. Bağırma, yüksek sesle konuşma,
                yüksek sesle şarkı söyleme, ses tellerini dinlendirmeden uzun
                süre boyunca konuşma yapılması ve çok tiz veya pes tonlarda uzun
                süreli olarak konuşulması, ses tellerinin bulunduğu bölgede
                inflamasyona neden olabilecek durumlar arasında yer alır.
                <br />
                <br />
                Larenjit bazı kişilerde kronikleşebilen bir hadisedir. Özellikle
                tütün ve alkol gibi zararlı alışkanlıkların varlığı, reflü ve
                çeşitli irritan kimyasallara maruz kalınması halinde uzun süreli
                larenjit durumları gelişebileceği için dikkatli olunmalıdır.
                <br />
                <br />
                Ses tellerinin bulunduğu yapılar birer cep veya katlantı
                şeklindedir. Bu katlantılar üzerinde gelişen nodül, kist veya
                polip gibi iyi huylu oluşumlar da ses kısıklığı nedenleri
                arasında yer alır. Vokal nodüller (şarkıcı nodülü), bu ceplerde
                karşılıklı ve çiftler halinde gelişen nodülleri tanımlar. Bu
                nodüllerin gelişimi, ses telleri üzerinde aşırı baskı oluşmasına
                neden olabilir. Vokal nodüllerden farklı olarak vokal kist
                gelişimi, ses tellerinin içinde gelişen sert doku şeklinde
                açıklanabilir. Vokal polipler ise katlantıların genellikle tek
                tarafında ortaya çıkar. Hem nodüller hem de polipler ses
                kısıklığına neden olabilen etkenler arasında bulunur.
                <br />
                <br />
                Ses tellerinin bulunduğu katlantılarda kanama meydana gelmesi
                bir diğer ses kısıklığı nedenidir. Vokal katlantı kanaması, bu
                dokunun yüzeyinde bulunan kan damarlarında yırtılma olduğu zaman
                oluşur. Hızla gelişen ses kısıklığı aynı zamanda şarkı söyleme
                işlevini de bir anda olumsuz yönde etkiler. Vokal katlantıda
                kanama nedeniyle gelişen ses kısıklığında kişiler, vakit
                kaybetmeden ses tellerini dinlendirmeye başlamalı ve en yakın
                sağlık kuruluşuna başvurmalıdır.
                <br />
                <br />
                Ses tellerinde tek ya da çift taraflı oluşabilen felçler, bu
                yapıların normal şekilde açılıp kapanmasını engelleyerek ses
                kısıklığına neden olabilir. Baş, boyun ve göğüs bölgesine alınan
                travmalar veya bu bölgelerde meydana gelen tümöral oluşumlar,
                ses tellerinde felç gelişimine neden olabilen durumlar arasında
                yer alır.
                <br />
                <br />
                İnme, Parkinson hastalığı veya spazmodik disfoni olarak
                isimlendirilen nörolojik rahatsızlıklar, bazı kişilerde beynin
                larinks bölgesindeki kasları kontrol eden kısımlarında çeşitli
                sorunlara neden olarak ses kısıklığı meydana getirebilir.
                <br />
                <br />
                Bir diğer ses kısıklığı nedeni olan reflü varlığında ise mide
                içeriğinin asidik yapısı, yemek borusuna geri kaçar. Bu asit
                yapıdaki içerik, larinks bölgesine ulaştığında bu bölgeyi tahriş
                ederek ses kısıklığına neden olabilir. Reflüye bağlı oluşan ses
                kısıklığı, özellikle sabah ilk uyanıldığı zaman belirgindir.
                <br />
                <br />
                Hem mevsimsel olarak oluşan hem de yıl boyu devam eden alerjik
                hadiseler, ses kısıklığı nedenleri arasında yer alır. Tiroit
                hastalıkları da ses kısıklığı sebebi olabilir. Özellikle uygun
                tedavi almamış hipotiroidi hastalarında ses kısıklığı ortaya
                çıkabilir.
              </p>
            </Level>
          </section>
          <section
            id="ses-kisikligi-en-cok-kimlerde-gorulmektedir"
            className={styles.typesHolder}
          >
            <Level>
              <Level>
                <Heading>Ses Kısıklığı En Çok Kimlerde Görülmektedir?</Heading>
              </Level>
              <p>
                Ses kısıklığı en sık olarak larinks bölgesinin inflamasyonunu
                tetikleyebilen faktörlere sahip kişilerde görülür. Soğuk
                algınlığı, bağırma veya yüksek sesle şarkı söylemeyi gerektiren
                meslekler, bakteriyel enfeksiyonlar veya alkol tüketimi olan
                kişilerde, ses kısıklığına karşı bir yatkınlık söz konusu
                olabilir. Bu faktörlere ek olarak, hava kirliliği bulunan
                bölgelerde yaşanması, çeşitli alerjen veya kimyasal maddelerin
                havayolu ile vücuda alınması, reflü, sık sinüs enfeksiyonu
                geçirilmesi, tütün kullanımı veya pasif içicilik gibi durumlar
                da ses kısıklığı riskini artırıcı özellik gösterebilen faktörler
                arasında yer alır.
                <br />
                <br />
                Ses kısıklığı çocuklarda da görülebilen bir durumdur. Özellikle
                kreş, anaokulu veya ilkokul gibi çocukların birbirine çok yakın
                olduğu ve bağırarak oyunlar oynadığı ortamlarda bulunan
                çocuklarda larenjit gelişimine karşı bir yatkınlık söz konusu
                olabilir. Küçük yaştaki bireylerde ses kısıklığı tespit edilmesi
                halinde ses tellerinin dinlendirilmesine özen gösterilmelidir.
                İlk aşamada ılık sıvı tüketimi ile desteklenebilecek ses
                kısıklığının geçmemesi veya ilerlemesi halinde ise sağlık
                kuruluşlarına başvurarak uzman hekimlerden destek alınması
                önerilir.
              </p>
              <StaticImage
                className={styles.image}
                src="../../../images/cozum/ses-kisikligi/portrait-of-a-sick-woman-covered-with-a-blanket-communicating-with-a-doctor-on-a-tablet-and-showing-.webp"
                alt="Boğazını tutan kadın görseli"
                width={792}
                height={430}
              />
            </Level>
          </section>
          <section
            id="ses-kisikligina-ne-iyi-gelir"
            className={styles.whyHolder}
          >
            <Level>
              <Level>
                <Heading>Ses Kısıklığına Ne İyi Gelir?</Heading>
              </Level>
              <p>
                Ses kısıklığının meydana gelmesi genellikle larenjit olarak
                isimlendirilen iltihabi durumdan kaynaklanır. Ses tellerinin
                aşırı kullanılması veya soğuk algınlığı gibi durumlarda,
                larenjit ortaya çıkabilir. Larinks bölgesinde yer alan ses
                telleri, konuşma sırasında düzgünce açılıp kapanma hareketi
                yapar. Bu hareket sırasında aralarından geçen hava, ses
                tellerinin titreştirilmesini ve sesin oluşturulmasını sağlar.
                Ses tellerinin bulunduğu bölgede meydana gelen inflamasyon ve
                ödem, havanın bu bölgeden geçişini engelleyerek sesin normalden
                daha kısık oluşmasına neden olabilir. Çeşitli uygulamalar, basit
                nedenlerle oluşan ses kısıklığı olgularında durumun kontrol
                altına alınmasına katkı sağlayabilir.
                <br />
                <br />
                Sesin dinlendirilmesi, ses kısıklığı gelişimi sonrasında
                atılabilecek adımların başında gelir. İstirahat ile birlikte ses
                tellerinin daha fazla tahriş olması da engellenmiş olur. 1-2 gün
                boyunca sadece gerekli durumlarda kısık sesle yapılacak konuşma
                şeklinde gerçekleştirilen ses tellerinin dinlendirilmesi, bu
                bölgede oluşan inflamasyonun dindirilmesine katkı sağlayabilir.
                Ancak bu istirahat sırasında fısıldama yapılmamasına dikkat
                edilmelidir. Fısıldama, bilinenin aksine ses telleri üzerinde
                normal konuşmaya göre daha fazla yük binmesine neden olabilir.
                Fısıldama sırasında ses tellerinin sıkı bir şekilde çekilmesi,
                ses kısıklığının iyileşme sürecini olumsuz yönde
                etkileyebileceği için dikkatli olunmalıdır.
                <br />
                <br />
                Larenjit, genellikle soğuk algınlığı gibi viral enfeksiyonlardan
                kaynaklanır. Dolayısıyla vücudun dinlendirilmesi ve yeterli sıvı
                alımının sağlanması, iyileşme sürecinin desteklenmesi için
                oldukça önemlidir.
                <br />
                <br />
                Sıcak bir banyo yapılması ses kısıklığına iyi gelebilen bir
                diğer uygulamadır. Sıcak banyo suyundan oluşan su buharı,
                bulunan ortamın nemlendirilmesini sağlayarak boğaz ve ses teli
                yapılarının da rahatlamasına katkı sağlayabilir. Bu uygulama
                yapılırken cilt yanıklarından kaçınmak adına suyun ideal ısıda
                olmasına dikkat edilmelidir.
                <br />
                <br />
                Yaşanılan ortamın nem oranının düşük olması nedeniyle kuruluk
                şikayetine bağlı oluşan ses kısıklığında, nemlendirici
                cihazların kullanımı fayda sağlayabilir. Gece vakti özellikle
                uyku sırasında boğaz bölgesinde kuruluk meydana gelmesi doğal
                bir durumdur. Uyku sırasında üretilen tükürük düzeyinin
                azalması, ağız ve boğaz bölgesinde bakteri birikimini
                kolaylaştırıcı etki yapabilir. Uyunan ortamın nemlendirilmesi bu
                durumun önüne geçerek, kuruluğa bağlı oluşan ses kısıklığının
                iyileşme sürecine katkı sağlayabilen bir yaklaşımdır.
                <br />
                <br />
                Tütün kullanımı gibi zararlı alışkanlıkları olan kişilerde ses
                kısıklığı gelişmesi halinde bu alışkanlıklarından en azından
                birkaç gün vazgeçilmesi önerilir. Tütün kullanımı sırasında
                boğazdan geçerek akciğerlere çekilen sigara dumanında, larinks
                bölgesinin tahrişine neden olabilecek çeşitli kimyasallar
                bulunur. Aynı zamanda bu ürünlerde bulunan nikotin, iyileşme
                sürecini de olumsuz yönde etkileyebileceği için bilinçli
                olunmalıdır.
                <br />
                <br />
                Tütün kullanımında olduğu gibi ses kısıklığı gelişen kişilerin
                alkol kullanımına da en azından ara vermesi önerilir. Alkol
                tüketimi, boğaz bölgesinde kuruluğa neden olarak ses tellerinde
                daha ileri hasar meydana gelmesini tetikleyebileceği için
                dikkatli olunmalıdır.
                <br />
                <br />
                Reflü nedeniyle ses kısıklığı gelişen kişilerin beslenme
                planlarını, bu durumlarını rahatlatacak şekilde değiştirmeleri
                önerilir. Özellikle baharatlı veya acı içeriğe sahip gıdalar,
                reflü şikayetini ve dolayısıyla bu probleme bağlı oluşabilecek
                ses kısıklığını artırabileceği için bilinçli olunmalıdır.
              </p>
            </Level>
          </section>
          <section className={styles.kidsHolder}>
            <Level>
              <Level>
                <Heading>
                  Bisolduo Şurup: Soğuk Algınlığının Sebep Olduğu Ses
                  Kısıklığını Rahatlatır
                </Heading>
              </Level>
            </Level>
            <div className={classNames("flexbox", styles.productHolder)}>
              <StaticImage
                className={styles.image}
                src="../../../images/product-bar-bisolduo-surup.png"
                alt="Bisolnatur ürün görseli"
                width={282}
                height={282}
              />
              <div className={styles.content}>
                <p>
                  Bisolduo şurup, saf doğal bal ve hatmi kökü kuru ekstresi
                  içeren doğal bir üründür. Bal, viskoz kıvamı ve yatıştırıcı
                  etkileri sayesinde kuru, boğaz ağrısını sakinleştirmeye
                  yardımcı olan iyi bilinen bir doğal bileşendir. Hatmi kökü,
                  mükemmel kaplama faydaları ile yoğun kıvamlı doğal bir
                  bileşendir. Aynı zamanda, balsam etkisi yaratmaya yardımcı
                  olan, yüksek oranda çözünebilir bir bileşendir. Gluten,
                  laktoz, alkol, yapay aroma ve koruyucu içermeyen Bisolduo
                  şurup, aynı zamanda hoş bir tada sahiptir. Yetişkinler ve 2
                  yaş üzerindeki çocuklar için kullanımı uygun olan Bisolduo
                  şurup, 2-6 yaş aralığındaki bireylerde günde en fazla 3 defa
                  olacak şekilde 2,5 ml (yarım tatlı kaşığı) ölçeğinde
                  tüketilebilir. Bu miktar 6-12 yaş çocuklar ve 12 yaş üstü
                  bireyler için 5 ml (bir tatlı kaşığı) olarak belirlenmiştir.
                  Yetişkinler ve adolesanlar, günde 6 defa 5 ml ölçeğinde
                  tüketilebilir. Bisolduo şurup, soğuk algınlığı nedeniyle
                  oluşan ses kısıklığı gibi boğaz rahatsızlığı belirtilerini
                  rahatlatır.
                </p>
              </div>
            </div>
          </section>
          <section className={styles.kaynakca}>
            <Level>
              <Level>
                <Heading id="kaynakca">Kaynakça</Heading>
              </Level>
            </Level>
            <p>
              1-
              https://www.verywellhealth.com/hoarseness-causes-and-treatment-2248928
            </p>
            <p>
              2- https://www.healthline.com/health/hoarseness
            </p>
            <p>
              3- https://www.healthline.com/health/laryngitis-2
            </p>
            <p>
              4- https://www.medicalnewstoday.com/articles/hoarse-voice
            </p>
            <p>
              5- https://www.healthline.com/health/how-to-get-your-voice-back#_noHeaderPrefixedContent
            </p>
            <p>
              6- https://www.healthline.com/health/home-remedies-for-laryngitis
            </p>
            <p>
              7- https://www.bisolnatur.com.tr/urunler/bisolduo-surup/
            </p>
          </section>
        </div>
        <div className="date">
          <p>Yayınlama Tarihi: 2022-01-27</p>
          <p>Son güncellenme Tarihi: 2022-01-27</p>
        </div>
      </Container>
      <ProductBar />
    </Layout>
  );
};
export default CoughPage;
